<!-- <svelte:options strictprops={false} /> -->

<script>
  import { onMount, tick } from "svelte";
  import { navigate } from "svelte-routing";
  import LoginForm from "#lib/LoginForm.svelte";
  import GoogleIcon from "@local/assets/icons/google.svg";
  import MicrosoftIcon from "@local/assets/icons/microsoft.svg";
  import NavbarUnauthenticated from "#lib/NavbarUnauthenticated.svelte";
  import { getStripeCheckoutSession } from "#src/api/stripe.js";
  import { user, profile, apiError, apiErrorCode, loading } from "#src/stores/auth.js";
  import isEmail from "@local/extensions/validators/is-email.js";
  import isLoggedIn from "#src/extensions/is-logged-in.js";

  // Undefined when the URL is exactly /login — the wildcard has nothing to
  // capture. Without a default, post-login navigation built "/undefined",
  // which matches no route and redirected to /not-found.
  export let path = "";
  export let onboarding = false;
  export let allowSignup = false;
  export let queryParams = {};

  let email = "";
  let password = "";
  let otp = "";
  let otpInput;
  let stripeSessionId = null;

  let state = "magiclink"; // ["magiclink", "password", "reset", "signup"]
  let firstSignIn = false;
  let sent = false;

  $: otpValid = !!otp;
  $: otpExpired = $apiError && $apiErrorCode === "otp_expired";
  $: accessDenied = $apiError && $apiErrorCode === "access_denied";
  $: buttonDisabled = isButtonDisabled($loading, state, email, password, otp, otpExpired);

  function isButtonDisabled(loading, state, email, password, otp, otpExpired) {
    if (loading) return true;
    if (otpExpired) return !isEmail(email);
    if (state === "magiclink") return !isEmail(email);
    if (state === "password") return !isEmail(email) || !password;
    if (state === "otp") return !isEmail(email) || !otp;
  }

  async function login() {
    // If we've already sent the request, we allow logging in with the OTP
    if (sent) {
      const p = await user.verifyOtp(email, otp);
      if (isLoggedIn($user) && state === "reset") {
        if (p?.has_onboarded === false) {
          await profile.onboard();
        }
        navigate("/passreset");
      } else if (p?.has_onboarded === false && p?.organization_id) {
        await profile.onboard();
        navigate("/passreset");
      } else if (isLoggedIn($user)) {
        navigate(path ? `/${path}` : "/");
      }
    } else if (state === "otp") {
      await user.verifyOtp(email, otp);
    } else if (state === "password") {
      await user.login(email, password);
      // Leave /login as soon as the session exists. Until the profile loads,
      // an authenticated user with no organization_id yet falls into a route
      // table that has no login route, so staying here means being redirected
      // to /not-found. The OTP branch above navigates for the same reason.
      if (isLoggedIn($user)) {
        navigate(path ? `/${path}` : "/");
      }
    } else if (state === "reset") {
      await user.resetPassword(email, "passreset");

      if (!$apiError) {
        sent = true;
        await tick();
        if (otpInput) {
          otpInput.focus();
        }
      }
    } else if (state === "signup") {
      await user.signup(email);
      if (!$apiError) {
        sent = true;
        await tick();
        if (otpInput) {
          otpInput.focus();
        }
      }
    } else if (state === "magiclink") {
      await user.sendMagicLink(email, path);
      if (!$apiError) {
        sent = true;
        await tick();
        if (otpInput) {
          otpInput.focus();
        }
      }
    }
  }

  async function oauthSignIn(provider) {
    await user.signInWithOAuth(provider, path);
  }

  function chooseLoginFlow(type) {
    state = type;
    if (type === "magiclink") {
      localStorage.setItem("login-pref", "magiclink");
    } else if (type === "password") {
      localStorage.setItem("login-pref", "password");
    }
  }

  onMount(async () => {
    if (isLoggedIn($user)) {
      await tick();
      navigate(path ? `/${path}` : "/");
    } else {
      if ($user?.is_anonymous) {
        await user.logout();
      }
      const hash = window.location.hash;
      if (queryParams["session_id"]) {
        const session = await getStripeCheckoutSession(queryParams["session_id"]);
        if (session.session?.status === "complete") {
          stripeSessionId = session.session.id;
          if (session.session.metadata?.create_user_account === "true") {
            email = session.session.metadata?.user_email || session.session.customer_details.email;
            chooseLoginFlow("magiclink");
            firstSignIn = true;
            sent = true;
          }
        }
      } else if (hash === "#otp") {
        chooseLoginFlow("otp");
      } else if (hash === "#password") {
        chooseLoginFlow("password");
      } else if (hash === "#one-time-code") {
        chooseLoginFlow("magiclink");
      } else if (allowSignup) {
        chooseLoginFlow("signup");
      } else {
        const pref = localStorage.getItem("login-pref");
        if (pref === "password") {
          state = "password";
        } else if (pref === "magiclink") {
          state = "magiclink";
        } else {
          state = "magiclink";
        }
      }
    }
  });
</script>

<NavbarUnauthenticated />
<LoginForm action={login}>
  {#if onboarding}
    <div class="text-sm">
      Your account has been created successfully! <br /> Check your email for a login link.
    </div>
  {/if}
  {#if !sent}
    {#if state === "password" || state === "magiclink"}
      <div class="text-xs text-left">Sign in to your account:</div>
    {/if}
    <div>
      <input
        class="border bg-gray-100 rounded w-full p-2 email-input"
        class:loading={$loading}
        type="email"
        autocomplete="username"
        placeholder="name@email.com"
        disabled={$loading || stripeSessionId}
        bind:value={email} />
    </div>
    {#if state === "password" && !otpExpired}
      <div>
        <input
          class="border bg-gray-100 rounded w-full p-2 password-input"
          class:loading={$loading}
          type="password"
          autocomplete="current-password"
          placeholder="password"
          disabled={$loading}
          bind:value={password} />
      </div>
    {/if}
    {#if state === "password" || state === "magiclink"}
      <div class="space-y-4 py-4">
        <div class="text-xs text-left">Or sign in with:</div>
        <button
          type="button"
          class="btn btn-lg w-full flex gap-2 justify-center items-center"
          on:click|preventDefault={() => oauthSignIn("google")}>
          <div><GoogleIcon /></div>
          <div>Continue with Google</div>
        </button>
        <button
          type="button"
          class="btn btn-lg w-full flex gap-2 justify-center items-center"
          on:click|preventDefault={() => oauthSignIn("azure")}>
          <div><MicrosoftIcon /></div>
          <div>Continue with Microsoft</div>
        </button>
      </div>
    {/if}
    {#if $apiError}
      {#if accessDenied}
        <div class="text-sm">
          This account does not have access. Please <button
            class="btn-text"
            on:click={() => navigate("/pricing")}>create an account</button>
          to continue.
        </div>
      {:else}
        <div class="text-red-600 text-sm">{$apiError}</div>
      {/if}
    {/if}
    {#if allowSignup}
      <p class="mt-2">
        Don't have an account? <button class="text-blue-500" on:click={() => chooseLoginFlow("signup")}
          >Sign up here.</button>
      </p>
    {/if}
    <button
      type="submit"
      class="btn btn-lg btn-primary w-full"
      class:loading={$loading}
      disabled={buttonDisabled}>
      {#if otpExpired}
        Re-send Link
      {:else if state === "magiclink"}
        Sign In with Email
      {:else if state === "password"}
        Sign In
      {:else if state === "reset"}
        Send Password Reset Link
      {:else if state === "signup"}
        Sign Up
      {/if}
    </button>
    {#if state === "password" && !otpExpired}
      <div class="text-right text-xs space-y-1">
        <div>
          <button class="btn-text" on:click={() => chooseLoginFlow("magiclink")}
            >Request one-time code</button>
        </div>
        <div>
          <button class="btn-text" on:click={() => chooseLoginFlow("reset")}>Forgot password?</button>
        </div>
      </div>
    {:else if state === "magiclink"}
      <div class="text-right text-xs">
        <div>
          <button class="btn-text" on:click={() => chooseLoginFlow("password")}>Sign in with password</button>
        </div>
      </div>
    {:else if state === "reset"}
      <div class="rounded bg-gray-100 py-4 px-6 text-left text-sm">
        <p>
          Enter email to receive password reset link, or <button
            class="btn-text contents choose-one-time-code-button"
            on:click={() => chooseLoginFlow("magiclink")}>sign in with a one-time code instead.</button>
        </p>
      </div>
    {/if}
  {:else}
    <div class="text-sm">
      <p class="mb-2">
        A 6-digit login code has been sent to <span class="font-bold">{email}</span>.
      </p>
      <p>Enter it below to log in:</p>
    </div>
    {#if $apiError}
      <div class="text-red-600 text-sm">{$apiError}</div>
    {/if}
    {#if ["magiclink", "reset", "signup"].includes(state)}
      <div>
        <input
          bind:this={otpInput}
          class="otp-input"
          class:loading={$loading}
          autocomplete="one-time-code"
          placeholder="123456"
          maxlength="6"
          size="6"
          disabled={$loading}
          bind:value={otp} />
      </div>
      {#if firstSignIn}
        <div class="space-y-4 py-4">
          <div class="text-xs text-left">Or sign in with:</div>
          <button
            type="button"
            class="btn btn-lg w-full flex gap-2 justify-center items-center"
            on:click|preventDefault={() => oauthSignIn("google")}>
            <div><GoogleIcon /></div>
            <div>Continue with Google</div>
          </button>
          <button
            type="button"
            class="btn btn-lg w-full flex gap-2 justify-center items-center"
            on:click|preventDefault={() => oauthSignIn("azure")}>
            <div><MicrosoftIcon /></div>
            <div>Continue with Microsoft</div>
          </button>
        </div>
      {/if}
      <button
        type="submit"
        class="btn btn-lg btn-primary w-full"
        class:loading={$loading}
        disabled={$loading || !otpValid}>
        {#if state === "magiclink"}
          Sign in
        {:else if state === "signup"}
          Complete Sign Up
        {:else}
          Go to Password Reset
        {/if}
      </button>
    {/if}
  {/if}
</LoginForm>

<style>
  .loading {
    opacity: 0.5;
  }

  .otp-input {
    @apply p-2 border rounded font-mono bg-gray-100;
    font-size: 2.5rem;
    text-align: left;
    min-width: 0;
    max-width: 100%;
  }
</style>
